import { z } from 'zod';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { ApiError, apiClient, prepareApiRequest } from './client';

/**
 * Firm settings → Vendor knowledge base (E13; T-0206 and T-0207).
 *
 * Contract: `docs/vendor-knowledge/technical/E13-F01…F07-spec.md` §3,
 * implemented in `apps/api/src/routes/vendor-knowledge-*.ts`. Every route
 * is firm-root under `/api/v1/vendor-knowledge`. Every firm user reads the
 * catalog and confirmed items; review, sources, captures, protected names
 * and every write need curation authority (the server decides; the screen
 * only hides what a consultant cannot use).
 *
 * Counts are always the server's (`/summary`, `/review`, `/tree`): the
 * screens never count rows themselves.
 */

const BASE = '/api/v1/vendor-knowledge';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

const Ref = z.object({ id: z.string(), name: z.string().nullable() });
export type KbRef = z.infer<typeof Ref>;

const UserRef = z.object({ id: z.string(), name: z.string() }).nullable();

const ModuleRef = z
  .object({ id: z.string().nullable(), name: z.string().nullable(), label: z.string().nullable() })
  .nullable();
export type KbModuleRef = z.infer<typeof ModuleRef>;

const Range = z.object({ start: z.number(), end: z.number() });
export type KbRange = z.infer<typeof Range>;

/** `renderCitation` (ADR-018 §5): the canonical text and its parts. */
export const CitationSchema = z.object({
  text: z.string(),
  parts: z.object({
    number: z.string().optional(),
    excerpt: z.string().optional(),
    answer_label: z.string().optional(),
    source_label: z.string(),
    url: z.string().optional(),
    date: z.string(),
    locator_label: z.string().optional(),
    confidential: z.boolean(),
  }),
});
export type KbCitation = z.infer<typeof CitationSchema>;

const SourceInfo = z.object({
  id: z.string().optional(),
  kind: z.string(),
  title: z.string().nullable(),
  url: z.string().nullable(),
  file_name: z.string().nullable().optional(),
  confidential: z.boolean().optional(),
});
export type KbSourceInfo = z.infer<typeof SourceInfo>;

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

const TreeProduct = z.object({
  id: z.string(),
  name: z.string(),
  platform_type: z.string(),
  status: z.string(),
  confirmed_items: z.number(),
});

export const TreeSchema = z.object({
  vendors: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.string(),
      platform_type: z.string().nullable(),
      products: z.array(TreeProduct),
    }),
  ),
  partners: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.string(),
      product_ids: z.array(z.string()),
    }),
  ),
  can_curate: z.boolean(),
});
export type KbTree = z.infer<typeof TreeSchema>;
export type KbTreeVendor = KbTree['vendors'][number];
export type KbTreePartner = KbTree['partners'][number];

const Domain = z.object({
  id: z.string(),
  host: z.string(),
  include_subdomains: z.boolean(),
  status: z.string(),
  origin: z.string(),
});
export type KbDomain = z.infer<typeof Domain>;

const Product = z.object({
  id: z.string(),
  vendor_id: z.string(),
  name: z.string(),
  platform_type: z.string(),
  status: z.string(),
  market_description: z.string().nullable(),
  market_description_origin: z.string().nullable(),
  confirmed_items: z.number().optional(),
});
export type KbProduct = z.infer<typeof Product>;

export const VendorSchema = z.object({
  vendor: z.object({
    id: z.string(),
    name: z.string(),
    platform_type: z.string().nullable(),
    status: z.string(),
    retire_reason: z.string().nullable(),
    created_at: z.string(),
  }),
  domains: z.array(Domain),
  products: z.array(Product),
  partners: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.string(),
      product_ids: z.array(z.string()),
    }),
  ),
  company_items_count: z.number(),
  upload_links_count: z.number(),
});
export type KbVendor = z.infer<typeof VendorSchema>;

// ---------------------------------------------------------------------------
// Counts (GET /summary, GET /review) and the import history (GET /imports)
// ---------------------------------------------------------------------------

export const SummaryModuleSchema = z.object({
  product_id: z.string().nullable(),
  library_module_id: z.string().nullable(),
  label: z.string().nullable(),
  knowledge_items: z.number(),
  requirement_answers: z.number(),
  answered_requirements: z.number(),
});
export type KbSummaryModule = z.infer<typeof SummaryModuleSchema>;

export const RecentRunSchema = z.object({
  run_id: z.string(),
  kind: z.string(),
  trigger: z.string(),
  status: z.string(),
  outcome: z.string().nullable(),
  counts: z.object({ proposed: z.number(), offered_citations: z.number(), flagged: z.number() }),
  finished_at: z.string(),
  source: z.object({
    id: z.string(),
    kind: z.string(),
    title: z.string().nullable(),
    url: z.string().nullable(),
  }),
  vendor: Ref.nullable(),
});
export type KbRecentRun = z.infer<typeof RecentRunSchema>;

export const SummarySchema = z.object({
  knowledge_items: z.number(),
  requirement_answers: z.number(),
  answered_requirements: z.number(),
  answer_partners: z.number(),
  last_checked_at: z.string().nullable(),
  modules: z.array(SummaryModuleSchema).optional(),
  kept_items: z.number().optional(),
  web_pages: z
    .object({ registered: z.number(), checked: z.number(), due: z.number(), gone: z.number() })
    .optional(),
  documents: z.object({ total: z.number(), confidential: z.number() }).optional(),
  recent: z.array(RecentRunSchema).optional(),
});
export type KbSummary = z.infer<typeof SummarySchema>;

export const REVIEW_KEYS = [
  'proposed',
  'held',
  'flagged',
  'received',
  'waiting_to_read',
  'source_problems',
  'due_for_review',
] as const;
export type KbReviewKey = (typeof REVIEW_KEYS)[number];

export const ReviewSchema = z.object({
  groups: z.array(z.object({ key: z.string(), count: z.number() })),
  by_vendor: z.array(z.object({ vendor_id: z.string(), count: z.number() })),
});
export type KbReview = z.infer<typeof ReviewSchema>;

/** A review group's count, as the server returned it (0 when absent). */
export function reviewCount(review: KbReview | undefined, key: KbReviewKey): number {
  return review?.groups.find((g) => g.key === key)?.count ?? 0;
}

export const ImportsSchema = z.object({
  runs: z.array(
    z.object({
      id: z.string(),
      stage: z.string(),
      status: z.string(),
      file_checksum: z.string(),
      exported_at: z.string(),
      started_at: z.string(),
      finished_at: z.string().nullable(),
      actor: z.object({ id: z.string(), name: z.string() }),
      report: z.record(z.unknown()),
    }),
  ),
});
export type KbImports = z.infer<typeof ImportsSchema>;
export type KbImportRun = KbImports['runs'][number];

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

const Requirement = z.object({
  library_item_id: z.string().nullable(),
  library_item_number: z.string().nullable(),
  library_text: z.string().nullable(),
  answer_code: z.string().nullable(),
  answer_label: z.string().nullable(),
});
export type KbRequirement = z.infer<typeof Requirement>;

export const ItemRowSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  kind: z.string(),
  vendor_id: z.string(),
  product: Ref.nullable(),
  partner: Ref.optional(),
  module: ModuleRef,
  process_area: z.string().nullable(),
  statement: z.string().nullable(),
  excerpt: z.string(),
  source_kind: z.string(),
  as_of: z.string().nullable(),
  flag_reason: z.string().optional(),
  hold_reason: z.string().optional(),
  confidential: z.boolean(),
  due_for_review: z.boolean().optional(),
  requirement: Requirement.optional(),
});
export type KbItemRow = z.infer<typeof ItemRowSchema>;

export const ItemPageSchema = z.object({
  items: z.array(ItemRowSchema),
  next_cursor: z.string().nullable(),
});
export type KbItemPage = z.infer<typeof ItemPageSchema>;

export const ItemDetailSchema = z.object({
  item: z.object({
    id: z.string(),
    number: z.string().nullable(),
    status: z.string(),
    kind: z.string(),
    vendor: Ref,
    product: Ref.nullable(),
    partner: Ref.nullable(),
    module: ModuleRef,
    process_area: z.string().nullable(),
    statement: z.string().nullable(),
    excerpt_text: z.string().nullable(),
    answer_code: z.string().nullable(),
    supersedes_item_id: z.string().nullable(),
    confirmed: z.object({ by: UserRef, at: z.string() }).nullable(),
    statement_corrected: z.object({ by: UserRef, at: z.string() }).nullable(),
    retired: z.object({ reason: z.string().nullable(), by: UserRef, at: z.string() }).nullable(),
    source_version_id: z.string().optional(),
    segment_id: z.string().optional(),
    origin: z.string().optional(),
    created: z.object({ by: UserRef, at: z.string() }).optional(),
    redaction_reviewed: z.object({ by: UserRef, at: z.string() }).nullable().optional(),
  }),
  citation: CitationSchema,
  further_citations: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      citation: CitationSchema,
      flag_reason: z.string().optional(),
      decided: z.object({ by: UserRef, at: z.string() }).nullable(),
    }),
  ),
  history: z.array(
    z.object({
      id: z.string(),
      number: z.string().nullable(),
      status: z.string(),
      relation: z.string(),
    }),
  ),
  flag: z
    .object({
      reason: z.string(),
      detail: z.record(z.unknown()).nullable(),
      flagged_at: z.string().nullable(),
      passages: z
        .object({
          before: z.object({ segment_id: z.string(), text: z.string() }).nullable(),
          now: z.array(z.object({ segment_id: z.string(), text: z.string() })),
        })
        .optional(),
    })
    .optional(),
  as_of: z.string().nullable(),
  corrections: z.array(
    z.object({
      previous_statement: z.string().nullable(),
      statement: z.string().nullable(),
      by: UserRef,
      at: z.string(),
    }),
  ),
  requirement: Requirement.optional(),
  /** T-0208 — future-state maps whose latest version cites this item: a count, never which. */
  future_maps: z.number().optional(),
});
export type KbItemDetail = z.infer<typeof ItemDetailSchema>;

// ---------------------------------------------------------------------------
// Review rows
// ---------------------------------------------------------------------------

export const ProposalSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  kind: z.string(),
  vendor_id: z.string(),
  product_id: z.string().nullable(),
  partner_id: z.string().nullable(),
  library_module_id: z.string().nullable(),
  module_label: z.string().nullable(),
  process_area: z.string().nullable(),
  library_item_id: z.string().nullable(),
  answer_code: z.string().nullable(),
  statement: z.string().nullable(),
  ai_statement: z.string().nullable(),
  source_version_id: z.string(),
  segment_id: z.string(),
  excerpt_start: z.number(),
  excerpt_end: z.number(),
  excerpt_text: z.string(),
  redactions: z.array(Range),
  origin: z.string(),
  run_id: z.string().nullable(),
  confidence: z.number().nullable(),
  confidence_word: z.string().nullable(),
  preselected: z.boolean(),
  hold_reason: z.string().nullable(),
  redaction_reviewed_at: z.string().nullable(),
  supersedes_item_id: z.string().nullable(),
  created_at: z.string(),
  product: Ref.nullable().optional(),
  partner: Ref.nullable().optional(),
  module: ModuleRef.optional(),
  locator_label: z.string().nullable().optional(),
  library_suggestions: z
    .array(z.object({ item_id: z.string(), number: z.string(), text: z.string() }))
    .optional(),
  source: SourceInfo.optional(),
});
export type KbProposal = z.infer<typeof ProposalSchema>;

export const OfferedCitationSchema = z.object({
  id: z.string(),
  item: z.object({
    id: z.string(),
    number: z.string().nullable(),
    status: z.string(),
    statement: z.string().nullable(),
    excerpt: z.string(),
  }),
  excerpt_text: z.string(),
  locator_label: z.string().nullable(),
  confidence: z.number().nullable(),
  confidence_word: z.string().nullable(),
  origin: z.string(),
  run_id: z.string().nullable(),
  created_at: z.string(),
});
export type KbOfferedCitation = z.infer<typeof OfferedCitationSchema>;

export const ProposedGroupSchema = z.object({
  run: z
    .object({
      id: z.string(),
      kind: z.string(),
      trigger: z.string(),
      status: z.string(),
      outcome: z.string().nullable(),
    })
    .nullable(),
  vendor_id: z.string(),
  source: SourceInfo,
  version: z.object({ id: z.string(), version_no: z.number(), captured_at: z.string() }),
  counts: z.object({ items: z.number(), citations: z.number(), preselected: z.number() }),
  latest_at: z.string(),
  items: z.array(ProposalSchema),
  citations: z.array(OfferedCitationSchema),
});
export type KbProposedGroup = z.infer<typeof ProposedGroupSchema>;

export const FlaggedRowSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  kind: z.string(),
  vendor_id: z.string(),
  product: Ref.nullable(),
  module: ModuleRef,
  process_area: z.string().nullable(),
  statement: z.string().nullable(),
  excerpt_text: z.string(),
  as_of: z.string().nullable(),
  source: SourceInfo,
  citation: CitationSchema,
  flag: z
    .object({
      reason: z.string().nullable(),
      detail: z.record(z.unknown()).nullable(),
      flagged_at: z.string().nullable(),
    })
    .optional(),
});
export type KbFlaggedRow = z.infer<typeof FlaggedRowSchema>;

const page = <T extends z.ZodTypeAny>(row: T) =>
  z.object({ key: z.string(), rows: z.array(row), next_cursor: z.string().nullable() });

export const ProposedPageSchema = page(ProposedGroupSchema);
export const HeldPageSchema = page(ProposalSchema);
export const FlaggedPageSchema = page(FlaggedRowSchema);

export const ConfirmReplySchema = z.object({
  confirmed: z.array(z.object({ id: z.string(), number: z.string() })),
  became_citation: z.array(
    z.object({
      id: z.string(),
      item_id: z.string(),
      item_number: z.string().nullable(),
      citation_id: z.string().nullable(),
    }),
  ),
  skipped: z.array(z.object({ id: z.string(), reason: z.string() })),
  duplicate_mode: z.string().optional(),
});
export type KbConfirmReply = z.infer<typeof ConfirmReplySchema>;

export const DeclineReplySchema = z.object({
  declined: z.array(z.string()),
  skipped: z.array(z.object({ id: z.string(), reason: z.string() })),
});

const Loose = z.record(z.unknown());

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

export interface ItemListParams {
  vendor_id?: string;
  product_id?: string;
  partner_id?: string;
  library_module_id?: string;
  kind?: string;
  q?: string;
  status?: string;
}

export const vendorKnowledgeKeys = {
  all: ['vendor-knowledge'] as const,
  tree: () => [...vendorKnowledgeKeys.all, 'tree'] as const,
  vendor: (id: string) => [...vendorKnowledgeKeys.all, 'vendor', id] as const,
  summary: (scope: { vendor_id?: string; partner_id?: string }) =>
    [...vendorKnowledgeKeys.all, 'summary', scope] as const,
  review: (vendorId: string | null) => [...vendorKnowledgeKeys.all, 'review', vendorId] as const,
  reviewRows: (key: KbReviewKey, vendorId: string | null) =>
    [...vendorKnowledgeKeys.all, 'review-rows', key, vendorId] as const,
  items: (params: ItemListParams) => [...vendorKnowledgeKeys.all, 'items', params] as const,
  item: (id: string) => [...vendorKnowledgeKeys.all, 'item', id] as const,
  imports: () => [...vendorKnowledgeKeys.all, 'imports'] as const,
  settings: () => [...vendorKnowledgeKeys.all, 'settings'] as const,
  sources: (vendorId: string) => [...vendorKnowledgeKeys.all, 'sources', vendorId] as const,
  source: (id: string) => [...vendorKnowledgeKeys.all, 'source', id] as const,
  segments: (versionId: string) => [...vendorKnowledgeKeys.all, 'segments', versionId] as const,
};

function idem(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `vk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useKbTree() {
  return useQuery({
    queryKey: vendorKnowledgeKeys.tree(),
    queryFn: () => apiClient(`${BASE}/tree`, TreeSchema),
  });
}

export function useKbVendor(id: string | null) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.vendor(id ?? ''),
    queryFn: () => apiClient(`${BASE}/vendors/${encodeURIComponent(id!)}`, VendorSchema),
    enabled: !!id,
  });
}

export function useKbSummary(scope: { vendor_id?: string; partner_id?: string }) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.summary(scope),
    queryFn: () => apiClient(`${BASE}/summary`, SummarySchema, { query: scope }),
  });
}

/** Counts per review group (curators only). */
export function useKbReview(vendorId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.review(vendorId),
    queryFn: () =>
      apiClient(`${BASE}/review`, ReviewSchema, {
        query: vendorId ? { vendor_id: vendorId } : undefined,
      }),
    enabled,
  });
}

function useReviewPages<T extends z.ZodTypeAny>(
  key: KbReviewKey,
  schema: T,
  vendorId: string,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: vendorKnowledgeKeys.reviewRows(key, vendorId),
    queryFn: ({ pageParam }) =>
      apiClient(`${BASE}/review/${key}`, schema, {
        query: { vendor_id: vendorId, cursor: pageParam ?? undefined },
      }) as Promise<z.infer<T>>,
    initialPageParam: null as string | null,
    getNextPageParam: (last: { next_cursor: string | null }) => last.next_cursor,
    enabled,
  });
}

export function useKbProposedGroups(vendorId: string, enabled: boolean) {
  return useReviewPages('proposed', ProposedPageSchema, vendorId, enabled);
}
export function useKbHeld(vendorId: string, enabled: boolean) {
  return useReviewPages('held', HeldPageSchema, vendorId, enabled);
}
export function useKbFlagged(vendorId: string, enabled: boolean) {
  return useReviewPages('flagged', FlaggedPageSchema, vendorId, enabled);
}
export function useKbDue(vendorId: string, enabled: boolean) {
  return useReviewPages('due_for_review', FlaggedPageSchema, vendorId, enabled);
}

/** The rows of every loaded page, in order. */
export function rowsOf<T>(data: InfiniteData<{ rows: T[] }> | undefined): T[] {
  return data ? data.pages.flatMap((p) => p.rows) : [];
}

const ITEM_PAGE = 200;

/** Items in the server's order (module, process area), a page at a time. */
export function useKbItems(params: ItemListParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: vendorKnowledgeKeys.items(params),
    queryFn: ({ pageParam }) =>
      apiClient(`${BASE}/items`, ItemPageSchema, {
        query: { ...params, limit: ITEM_PAGE, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

export function useKbItem(id: string | null) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.item(id ?? ''),
    queryFn: () => apiClient(`${BASE}/items/${encodeURIComponent(id!)}`, ItemDetailSchema),
    enabled: !!id,
  });
}

export const SettingsSchema = z.object({
  refresh_days: z.number(),
  review_after_days: z.number(),
  link_ttl_days: z.number(),
});
export type KbSettings = z.infer<typeof SettingsSchema>;

export function useKbSettings() {
  return useQuery({
    queryKey: vendorKnowledgeKeys.settings(),
    queryFn: () => apiClient(`${BASE}/settings`, SettingsSchema),
  });
}

export function useKbImports(enabled = true) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.imports(),
    queryFn: () => apiClient(`${BASE}/imports`, ImportsSchema),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Sources, versions and captured text (curators; E13-F04 §3.1)
// ---------------------------------------------------------------------------

export const SourceRowSchema = z.object({
  id: z.string(),
  kind: z.string(),
  title: z.string().nullable(),
  url: z.string().nullable(),
  vendor: Ref.nullable().optional(),
  product: Ref.nullable(),
  status: z.string(),
  last_checked_at: z.string().nullable(),
  last_outcome: z.string().nullable(),
  next_check_at: z.string().nullable(),
  current_version_no: z.number().nullable(),
  // SQL expressions: null when the source has no outcome yet.
  waiting_to_read: z.boolean().nullable(),
  problem: z.boolean().nullable().optional(),
  confidential: z.boolean().optional(),
});
export type KbSourceRow = z.infer<typeof SourceRowSchema>;

export const SourcesSchema = z.object({ sources: z.array(SourceRowSchema) });

export const RunSchema = z
  .object({
    id: z.string(),
    kind: z.string(),
    trigger: z.string(),
    status: z.string(),
    outcome: z.string().nullable(),
    version_id: z.string().nullable(),
    counts: z.record(z.unknown()),
    estimate_cents: z.number().nullable(),
    cost_cents: z.number(),
    error_code: z.string().nullable(),
    created_at: z.string(),
    finished_at: z.string().nullable(),
  })
  .passthrough();
export type KbRun = z.infer<typeof RunSchema>;

export const SourceDetailSchema = z.object({
  source: SourceRowSchema.extend({
    origin: z.string(),
    outside_domain_reason: z.string().nullable(),
    refresh_days: z.number().nullable(),
    effective_refresh_days: z.number(),
    current_version_id: z.string().nullable(),
    current_version_checked_at: z.string().nullable(),
    registered_at: z.string(),
    retire_reason: z.string().nullable(),
    active_run: RunSchema.nullable(),
  }),
  versions: z.array(
    z.object({
      id: z.string(),
      version_no: z.number(),
      captured_at: z.string(),
      http_status: z.number().nullable(),
      final_url: z.string().nullable(),
      title: z.string().nullable(),
      segment_count: z.number(),
      changed_segments: z.number(),
      read: z.boolean(),
    }),
  ),
  runs: z.array(RunSchema),
});
export type KbSourceDetail = z.infer<typeof SourceDetailSchema>;

const Locator = z
  .object({
    kind: z.string(),
    heading_path: z.array(z.string()).optional(),
    page: z.number().optional(),
    sheet: z.string().optional(),
    row: z.number().optional(),
    section: z.string().nullable().optional(),
    paragraph: z.number().optional(),
  })
  .passthrough();

export const SegmentsSchema = z.object({
  segments: z.array(
    z.object({
      id: z.string(),
      ordinal: z.number(),
      locator: Locator,
      text: z.string(),
      changed_from_previous: z.boolean(),
    }),
  ),
});
export type KbSegment = z.infer<typeof SegmentsSchema>['segments'][number];

export function useKbSources(vendorId: string | null, enabled = true) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.sources(vendorId ?? ''),
    queryFn: () =>
      apiClient(`${BASE}/sources`, SourcesSchema, { query: { vendor_id: vendorId ?? undefined } }),
    enabled: enabled && !!vendorId,
  });
}

export function useKbSource(id: string | null) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.source(id ?? ''),
    queryFn: () => apiClient(`${BASE}/sources/${encodeURIComponent(id!)}`, SourceDetailSchema),
    enabled: !!id,
  });
}

export function useKbSegments(versionId: string | null) {
  return useQuery({
    queryKey: vendorKnowledgeKeys.segments(versionId ?? ''),
    queryFn: () =>
      apiClient(`${BASE}/versions/${encodeURIComponent(versionId!)}/segments`, SegmentsSchema),
    enabled: !!versionId,
    // A version never changes (rule 3).
    staleTime: Infinity,
  });
}

// ---------------------------------------------------------------------------
// Writes. Each carries its own Idempotency-Key; on success every knowledge
// base read is refetched, so counts and lists come back from the server.
// ---------------------------------------------------------------------------

function post<T>(path: string, schema: z.ZodType<T>, body: unknown = {}) {
  return apiClient(`${BASE}${path}`, schema, { method: 'POST', body, idempotencyKey: idem() });
}

function useKbWrite<V, T>(fn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: vendorKnowledgeKeys.all }),
  });
}

export function useCreateVendor() {
  return useKbWrite(
    (body: {
      name: string;
      platform_type: string;
      domains?: Array<{ host: string; include_subdomains: boolean }>;
    }) => post('/vendors', z.object({ id: z.string() }), body),
  );
}

export function useAddDomain() {
  return useKbWrite((v: { vendorId: string; host: string }) =>
    post(`/vendors/${v.vendorId}/domains`, Loose, { host: v.host, include_subdomains: true }),
  );
}

export function useDecideDomain() {
  return useKbWrite((v: { domainId: string; verb: 'approve' | 'reject'; reason?: string }) =>
    post(
      `/domains/${v.domainId}:${v.verb}`,
      Loose,
      v.verb === 'reject' ? { reason: v.reason ?? '' } : {},
    ),
  );
}

export function useCreateProduct() {
  return useKbWrite((v: { vendorId: string; name: string; platform_type: string }) =>
    post(`/vendors/${v.vendorId}/products`, Loose, {
      name: v.name,
      platform_type: v.platform_type,
    }),
  );
}

/** A new partner linked to products, or an existing one linked to more. */
export function useAddPartner() {
  return useKbWrite(
    async (v: { partnerId: string | null; name?: string; productIds: string[] }) => {
      if (!v.partnerId) {
        return post('/partners', Loose, { name: v.name, product_ids: v.productIds });
      }
      for (const productId of v.productIds) {
        await post(`/partners/${v.partnerId}/products`, Loose, { product_id: productId });
      }
      return {};
    },
  );
}

export function useConfirmItems() {
  return useKbWrite((v: { ids: string[]; acknowledge?: string[] }) =>
    post('/items:confirm', ConfirmReplySchema, {
      ids: v.ids,
      ...(v.acknowledge?.length ? { acknowledge: v.acknowledge } : {}),
    }),
  );
}

export function useDeclineItems() {
  return useKbWrite((v: { ids: string[]; reason_code: string; reason_text?: string }) =>
    post('/items:decline', DeclineReplySchema, v),
  );
}

/** Edit a proposal (statement, kind, filing, library requirement, a narrower quote). */
export function usePatchProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      apiClient(`${BASE}/items/${v.id}`, ProposalSchema, {
        method: 'PATCH',
        body: v.body,
        idempotencyKey: idem(),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [...vendorKnowledgeKeys.all, 'review-rows'] }),
  });
}

export function useItemAction() {
  return useKbWrite(
    (v: {
      id: string;
      verb: 'retire' | 'clear-flag' | 'correct-statement' | 'supersede';
      body: Record<string, unknown>;
    }) => post(`/items/${v.id}:${v.verb}`, Loose, v.body),
  );
}

export function useAddCitation() {
  return useKbWrite((v: { itemId: string; body: Record<string, unknown> }) =>
    post(`/items/${v.itemId}/citations`, Loose, v.body),
  );
}

export function useCreateItem() {
  return useKbWrite((body: Record<string, unknown>) =>
    post(
      '/items',
      z.object({ id: z.string(), status: z.string(), hold_reason: z.string().optional() }),
      body,
    ),
  );
}

export const SplitReplySchema = z.object({ item: z.object({ id: z.string() }).passthrough() });

export function useCitationAction() {
  return useKbWrite(
    (v: { id: string; verb: 'accept' | 'decline' | 'split'; body?: Record<string, unknown> }) =>
      post(`/citations/${v.id}:${v.verb}`, Loose, v.body ?? {}),
  );
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** The API's `details.reason` (`vk.name_taken`, ...), when there is one. */
export function errorReason(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const reason = err.details?.['reason'];
  return typeof reason === 'string' ? reason : null;
}

export function errorMessage(err: unknown, fallback = 'Something went wrong. Try again.'): string {
  if (err instanceof ApiError && err.message) return err.message;
  return fallback;
}

// ===========================================================================
// T-0207: sources, web search, documents, upload links, names, settings
// ===========================================================================

/** Whether a read failed because the server has no such route yet (an older API). */
export function isMissingRoute(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export function useRegisterSource() {
  return useKbWrite(
    (body: {
      vendor_id: string;
      product_id?: string | null;
      url: string;
      outside_domain_reason?: string;
      origin?: 'curator' | 'web_search';
    }) =>
      post('/sources', z.object({ source_id: z.string(), run_id: z.string().nullable() }), body),
  );
}

export function useSourceAction() {
  return useKbWrite(
    (v: {
      id: string;
      verb: 'check-now' | 'read-now' | 'retire' | 'restore';
      body?: Record<string, unknown>;
    }) => post(`/sources/${v.id}:${v.verb}`, Loose, v.body ?? {}),
  );
}

export function usePatchSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      apiClient(`${BASE}/sources/${v.id}`, Loose, {
        method: 'PATCH',
        body: v.body,
        idempotencyKey: idem(),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: vendorKnowledgeKeys.all }),
  });
}

export const SearchReplySchema = z.object({
  candidates: z.array(
    z.object({
      url: z.string(),
      title: z.string(),
      snippet: z.string(),
      on_vendor_domains: z.boolean(),
      already_registered_source_id: z.string().nullable(),
      reason: z.string().nullable(),
    }),
  ),
  queries: z.number(),
  results: z.number().optional(),
  ranked: z.boolean().optional(),
  cost_cents: z.number(),
});
export type KbSearchReply = z.infer<typeof SearchReplySchema>;

/** Web search on request: the candidates live only on the screen, never stored (E13-F04 §3.3). */
export function useWebSearch() {
  return useMutation({
    // The candidates live on the screen only: nothing is kept once it closes.
    gcTime: 0,
    mutationFn: (body: {
      vendor_id: string;
      product_id?: string;
      words?: string;
      include_other_sites?: boolean;
    }) =>
      apiClient(`${BASE}/web-search`, SearchReplySchema, {
        method: 'POST',
        body,
        idempotencyKey: idem(),
      }),
  });
}

// ---- Documents (T-0200) ---------------------------------------------------

export const ReceivedSchema = z.object({
  id: z.string(),
  vendor: Ref,
  product: Ref.nullable(),
  via: z.string(),
  link_id: z.string().nullable(),
  link_purpose: z.string().nullable(),
  uploaded_by: Ref.nullable(),
  file_name: z.string(),
  original_file_name: z.string().optional(),
  byte_size: z.number(),
  content_type: z.string(),
  sender_name: z.string().nullable(),
  sender_note: z.string().nullable(),
  scan_status: z.string(),
  status: z.string(),
  received_at: z.string(),
  decided_at: z.string().nullable(),
  reject_reason: z.string().nullable(),
  source_id: z.string().nullable(),
  version_id: z.string().nullable(),
});
export type KbReceived = z.infer<typeof ReceivedSchema>;

export function useKbReceived(vendorId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...vendorKnowledgeKeys.all, 'received', vendorId] as const,
    queryFn: () =>
      apiClient(`${BASE}/received`, z.object({ received: z.array(ReceivedSchema) }), {
        query: { vendor_id: vendorId, status: 'held' },
      }),
    enabled,
    retry: false,
  });
}

/**
 * A curator's upload (T-0200): the file is the request body, its name in
 * `X-File-Name` (percent-encoded), the curator's labels in the query.
 */
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      file: File;
      vendor_id: string;
      product_id?: string | null;
      title?: string;
      provided_by?: string;
      confidential: boolean;
      new_version_of_source_id?: string | null;
    }) => {
      const query: Record<string, string> = { vendor_id: v.vendor_id };
      if (v.new_version_of_source_id) {
        query['new_version_of_source_id'] = v.new_version_of_source_id;
      } else {
        if (v.product_id) query['product_id'] = v.product_id;
        if (v.title) query['title'] = v.title;
        if (v.provided_by) query['provided_by'] = v.provided_by;
        query['confidential'] = String(v.confidential);
      }
      return readBytes(v.file).then((buf) =>
        apiClient(
          `${BASE}/documents`,
          z.object({ receipt_id: z.string(), scan_status: z.string() }),
          {
            method: 'POST',
            body: new Uint8Array(buf),
            query,
            headers: {
              'Content-Type': 'application/octet-stream',
              'X-File-Name': encodeURIComponent(v.file.name),
            },
            idempotencyKey: idem(),
          },
        ),
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: vendorKnowledgeKeys.all }),
  });
}

export function useReceivedAction() {
  return useKbWrite((v: { id: string; verb: 'accept' | 'reject'; body: Record<string, unknown> }) =>
    post(`/received/${v.id}:${v.verb}`, Loose, v.body),
  );
}

// ---- Upload links (T-0201) ------------------------------------------------

export const UploadLinkSchema = z.object({
  id: z.string(),
  recipient_email: z.string(),
  purpose: z.string(),
  product: Ref.nullable(),
  issued_by: z.object({ id: z.string(), name: z.string().nullable() }),
  issued_at: z.string(),
  expires_at: z.string(),
  revoked_at: z.string().nullable(),
  status: z.string(),
  files_received: z.number(),
});
export type KbUploadLink = z.infer<typeof UploadLinkSchema>;

export function useKbUploadLinks(vendorId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...vendorKnowledgeKeys.all, 'upload-links', vendorId] as const,
    queryFn: () =>
      apiClient(
        '/api/v1/vendor-knowledge/upload-links',
        z.object({ links: z.array(UploadLinkSchema) }),
        {
          query: { vendor_id: vendorId },
        },
      ),
    enabled,
    retry: false,
  });
}

export function useSendUploadLink() {
  return useKbWrite(
    (body: {
      vendor_id: string;
      product_id?: string | null;
      purpose: string;
      recipient_email: string;
      recipient_name?: string;
    }) =>
      post(
        '/upload-links',
        z
          .object({ id: z.string(), url: z.string().optional(), expires_at: z.string() })
          .passthrough(),
        body,
      ),
  );
}

export function useRevokeUploadLink() {
  return useKbWrite((v: { id: string; reason: string }) =>
    post(`/upload-links/${v.id}:revoke`, Loose, { reason: v.reason }),
  );
}

// ---- Protected names (T-0197) --------------------------------------------

const NameRow = z.object({
  id: z.string(),
  name: z.string(),
  match_mode: z.string(),
  status: z.string(),
  added_by: z.string().nullable(),
  added_at: z.string(),
  decided_at: z.string().nullable(),
  applied_at: z.string().nullable(),
});

export const NamesSchema = z.object({
  from_clients: z.array(
    z.object({
      client_id: z.string(),
      name: z.string(),
      mode: z.string(),
      short_forms: z.array(
        z.object({
          text: z.string(),
          mode: z.string(),
          exception: z.boolean(),
          exception_id: z.string().optional(),
        }),
      ),
    }),
  ),
  curator: z.array(NameRow),
  import: z.array(NameRow.extend({ import_run_id: z.string().nullable() })),
  exceptions: z.array(
    z.object({
      id: z.string(),
      client_id: z.string().nullable(),
      text: z.string(),
      reason: z.string().nullable(),
      status: z.string(),
      added_by: z.string().nullable(),
      added_at: z.string(),
      decided_at: z.string().nullable(),
    }),
  ),
});
export type KbNames = z.infer<typeof NamesSchema>;

export function useKbNames(enabled = true) {
  return useQuery({
    queryKey: [...vendorKnowledgeKeys.all, 'names'] as const,
    queryFn: () => apiClient(`${BASE}/protected-names`, NamesSchema),
    enabled,
  });
}

export const RescanSchema = z.object({
  scanned: z.number(),
  redacted_proposals: z.number(),
  flagged_items: z.number(),
  flagged_citations: z.number(),
});
export type KbRescan = z.infer<typeof RescanSchema>;

export function useAddName() {
  return useKbWrite((body: { name: string }) =>
    post('/protected-names', z.object({ id: z.string(), rescan: RescanSchema }), body),
  );
}

export function useNameAction() {
  return useKbWrite((v: { id: string; verb: 'approve' | 'reject' | 'retire'; reason?: string }) =>
    post(
      `/protected-names/${v.id}:${v.verb}`,
      z
        .object({ id: z.string(), status: z.string(), rescan: RescanSchema.optional() })
        .passthrough(),
      v.verb === 'approve' ? {} : { reason: v.reason ?? '' },
    ),
  );
}

export function useNameException() {
  return useKbWrite(
    (v: { add: { client_id: string; text: string; reason: string } } | { retire: string }) =>
      'add' in v
        ? post('/protected-names/exceptions', Loose, v.add)
        : post(`/protected-names/exceptions/${v.retire}:retire`, Loose, {}),
  );
}

// ---- Settings (T-0197) ----------------------------------------------------

export function usePutSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { refresh_days?: number; review_after_days?: number }) =>
      apiClient(`${BASE}/settings`, SettingsSchema, {
        method: 'PUT',
        body,
        idempotencyKey: idem(),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: vendorKnowledgeKeys.all }),
  });
}

/**
 * Save a received file (curators; the server serves it only once its scan
 * is clean, rule 6). The bytes are fetched with the session's credentials
 * and handed to the browser as a download.
 */
export async function downloadReceivedFile(receiptId: string, fileName: string): Promise<void> {
  const { url, headers } = await prepareApiRequest(
    `${BASE}/received/${encodeURIComponent(receiptId)}/file`,
  );
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let message = 'The file could not be downloaded.';
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, 'download', message, '');
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** A file's bytes (FileReader where `Blob.arrayBuffer` is missing). */
export function readBytes(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
